import React, { useState } from 'react';
import styles from '../styles';
import useMediaQuery from '../hooks/useMediaQuery';
import { useLanguage } from '../contexts/LanguageContext';

const GOLD_GRADIENT = 'linear-gradient(135deg, #BF953F 0%, #FCF6BA 25%, #B38728 50%, #FBF5B7 75%, #AA771C 100%)';

const WhoIsItFor = () => {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const { t } = useLanguage();
    const [hovered, setHovered] = useState(null);

    const whoIsItForStyles = {
        container: {
            ...styles.section,
            background: 'linear-gradient(180deg, #FFFFFF 0%, #FFFBF0 100%)',
            padding: isMobile ? '3rem 1rem' : '4rem 2rem',
        },
        headline: {
            ...styles.h2,
            marginBottom: '2.5rem',
        },
        list: {
            listStyle: 'none',
            padding: 0,
            margin: 0,
            maxWidth: '1100px',
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '1.5rem',
        },
        card: (isHovered) => ({
            boxSizing: 'border-box',
            width: isMobile ? '100%' : 'calc((100% - 4.5rem) / 4)',
            minWidth: isMobile ? 0 : '240px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            padding: '1.75rem 1.25rem',
            borderRadius: '16px',
            // Shiny gold border: white fill on the padding box, gold gradient on the border box
            border: '2px solid transparent',
            background: `linear-gradient(#FFFFFF, #FFFFFF) padding-box, ${GOLD_GRADIENT} border-box`,
            boxShadow: isHovered
                ? '0 12px 28px rgba(179, 135, 40, 0.35)'
                : '0 4px 14px rgba(179, 135, 40, 0.18)',
            transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
            transition: 'transform 0.25s ease, box-shadow 0.25s ease',
        }),
        icon: {
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.6rem',
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, #FFF8E1 0%, #FDE9A9 100%)',
            border: '1px solid #E6C65C',
        },
        title: {
            fontSize: '1.15rem',
            fontWeight: 700,
            color: '#2D3748',
            letterSpacing: '0.2px',
            margin: 0,
        },
        divider: {
            width: '40px',
            height: '3px',
            borderRadius: '2px',
            background: GOLD_GRADIENT,
            margin: '0.75rem 0',
        },
        description: {
            fontSize: '0.95rem',
            lineHeight: '1.6',
            color: '#4A5568',
            margin: 0,
        },
    };

    const groups = [
        { key: 'seniors', icon: '👴' },
        { key: 'health', icon: '❤️' },
        { key: 'outdoor', icon: '⛰️' },
        { key: 'travellers', icon: '✈️' },
        { key: 'commuters', icon: '🚗' },
        { key: 'families', icon: '👪' },
        { key: 'loneWorkers', icon: '🏠' },
    ];

    const userGroups = groups.map(({ key, icon }) => ({
        icon,
        // The trailing colon is dropped since titles now stand on their own line
        title: t(`whoIsItFor.${key}Title`).replace(/:$/, ''),
        description: t(`whoIsItFor.${key}Description`),
    }));

    return (
        <section style={whoIsItForStyles.container}>
            <h2 style={whoIsItForStyles.headline}>{t('whoIsItFor.headline')}</h2>
            <ul style={whoIsItForStyles.list}>
                {userGroups.map((group, index) => (
                    <li
                        key={index}
                        style={whoIsItForStyles.card(hovered === index)}
                        onMouseEnter={() => setHovered(index)}
                        onMouseLeave={() => setHovered(null)}
                    >
                        <div style={whoIsItForStyles.icon} aria-hidden="true">{group.icon}</div>
                        <h3 style={whoIsItForStyles.title}>{group.title}</h3>
                        <div style={whoIsItForStyles.divider} />
                        <p style={whoIsItForStyles.description}>{group.description}</p>
                    </li>
                ))}
            </ul>
        </section>
    );
};

export default WhoIsItFor;
